import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAdminUsers, useAuth, useLogout } from "../api/hooks";
import { setThemePref, useThemePref, type ThemePref } from "../lib/theme";
import { errorText, useFlash } from "./Flash";

/** "Nick Arbenz" -> "NA"; "nick" -> "N". */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? [parts[0], parts[parts.length - 1]] : parts;
  return letters.map((p) => p[0]?.toUpperCase() ?? "").join("");
}

const THEMES: { value: ThemePref; label: string }[] = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "system", label: "System" },
];

/** Light, dark, or whatever the device is set to. */
function ThemeSwitch() {
  const pref = useThemePref();
  return (
    <div className="theme-switch" role="group" aria-labelledby="theme-label">
      <span id="theme-label">Theme</span>
      <div className="theme-options">
        {THEMES.map((t) => (
          <button
            key={t.value}
            type="button"
            aria-pressed={pref === t.value}
            title={t.value === "system" ? "Match your device's light or dark setting" : undefined}
            onClick={() => setThemePref(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * The account button at the right of the top bar: who's signed in (opening Account settings), Settings,
 * Import & backup, Admin (for admins, with the number of sign-ups waiting), the theme and Log out. A disclosure
 * (button + panel) rather than an ARIA menu, so Tab moves through it as usual. Closes on Escape, a click
 * outside or a change of page.
 */
export function AccountMenu({ year }: { year: number }) {
  const user = useAuth().data?.user ?? null;
  const logout = useLogout();
  const navigate = useNavigate();
  const flash = useFlash();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const onSettings = /^\/year\/\d{4}\/settings$/.test(pathname);
  const onImport = pathname === "/import";
  const onAdmin = pathname === "/admin";
  const admins = useAdminUsers(Boolean(user?.is_admin));
  const waiting = admins.data?.filter((u) => u.status === "pending").length ?? 0;

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;
    const outside = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  const signOut = () =>
    logout.mutate(undefined, {
      onSuccess: () => navigate("/login"),
      onError: (err) => flash(errorText(err), "error"),
    });
  const settings = `/year/${year}/settings`;

  return (
    <div className="account" ref={box}>
      <button
        ref={button}
        type="button"
        className={`account-btn${onSettings || onImport || onAdmin ? " active" : ""}`}
        aria-expanded={open}
        aria-controls="account-menu"
        aria-label={
          user
            ? `Account: ${user.display_name}${waiting ? `, ${waiting} sign-up${waiting === 1 ? "" : "s"} waiting` : ""}`
            : "Account"
        }
        onClick={() => setOpen((x) => !x)}
      >
        <span className="account-avatar" aria-hidden="true">
          {user ? (
            initials(user.display_name)
          ) : (
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <circle cx="12" cy="8.5" r="4" />
              <path d="M4 20.5c0-4.1 3.6-7 8-7s8 2.9 8 7z" />
            </svg>
          )}
        </span>
        <span className="account-label" aria-hidden="true">
          {user ? user.display_name : "Account"}
        </span>
        {waiting > 0 && (
          <span className="count-badge account-badge" aria-hidden="true">
            {waiting}
          </span>
        )}
        <span className="account-caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div className="account-menu" id="account-menu">
          {user && (
            <Link className="account-who" to={`${settings}#account`}>
              <b>{user.display_name}</b>
              <span>{user.email}</span>
              <span className="account-who-link">Account settings</span>
            </Link>
          )}
          <ul>
            <li>
              <Link to={settings} aria-current={onSettings ? "page" : undefined}>
                Settings
              </Link>
            </li>
            <li>
              <Link to="/import" aria-current={onImport ? "page" : undefined}>
                Import &amp; backup
              </Link>
            </li>
            {user?.is_admin && (
              <li>
                <Link to="/admin" aria-current={onAdmin ? "page" : undefined}>
                  Admin
                  {waiting > 0 && <span className="count-badge">{waiting}</span>}
                </Link>
              </li>
            )}
            <li>
              <ThemeSwitch />
            </li>
            <li>
              <button type="button" onClick={signOut} disabled={!user || logout.isPending}>
                Log out
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
